import { Link } from "@/lib/router-compat";
import { ArrowRight } from "lucide-react";
import { relatedTools } from "@/data/toolsCatalog";
import RelatedArticles from "./RelatedArticles";

interface Props {
  /** Path of the page rendering this strip, e.g. "/tools/cash-to-close". */
  currentPath: string;
  title?: string;
}

/**
 * Sibling-tool links rendered at the bottom of a guide so no page is an
 * orphan. Reads from src/data/toolsCatalog.ts.
 */
const RelatedTools = ({ currentPath, title = "Related tools" }: Props) => {
  const items = relatedTools(currentPath);
  if (items.length === 0) return <RelatedArticles currentPath={currentPath} />;


  return (
    <section className="border-t border-border bg-muted/30 py-10">
      <div className="container mx-auto max-w-5xl px-4">
        <div className="mb-5 flex items-baseline justify-between gap-4">
          <h2 className="text-lg font-bold text-foreground">{title}</h2>
          <Link
            to="/tools"
            className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
          >
            All tools <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {items.map((tool) => {
            const Icon = tool.icon;
            return (
              <Link
                key={tool.href}
                to={tool.href}
                className="group flex items-start gap-3 rounded-xl border border-border bg-card p-4 transition-colors hover:border-primary/40 hover:bg-muted/50"
              >
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Icon size={17} />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-foreground">
                    {tool.label}
                  </span>
                  <span className="block text-xs leading-snug text-muted-foreground">
                    {tool.description}
                  </span>
                </span>
              </Link>
            );
          })}
        </div>
      </div>
      <RelatedArticles currentPath={currentPath} />
    </section>
  );
};

export default RelatedTools;
